/**
 * Builds the Excel/Word/PDF export for the Asset Summary register.
 *
 * The on-screen table is the source of truth — not `summarizationTasks.output`,
 * which is a counts-and-narrative dump. The export is the unfiltered, ungrouped
 * register: one row per active (non-merged) asset, matching the table columns.
 *
 * Money is written as a real number (or blank when there is no figure) so the
 * practitioner can sort / sum in Excel. Valuation-source columns include every
 * source present on the job, not only the currently visible ones.
 */

import type { ExportContent, ExportSheet } from "@/utils/export";
import { ASSET_TYPE_LABEL, VALIDATION_FLAG_LABELS } from "./constants";
import {
  collectValuationSources,
  formatSourceType,
  getBestValue,
  getHighRecovery,
  getLowRecovery,
  getPractitionerValue,
  getRocapValue,
  getSelectedValue,
  getValuationForSource,
  isHiddenValuationSource,
} from "./helpers";
import type { AssetEntryUI } from "./types";

function money(value: number | null): number | "" {
  return value == null ? "" : value;
}

function flagsLabel(entry: AssetEntryUI): string {
  const flags = entry.validationFlags ?? [];
  if (flags.length === 0) return "";
  const seen = new Set<string>();
  const labels: string[] = [];
  for (const flag of flags) {
    if (seen.has(flag.type)) continue;
    seen.add(flag.type);
    labels.push(
      VALIDATION_FLAG_LABELS[flag.type] ??
        flag.type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
    );
  }
  return labels.join("; ");
}

function statusLabel(entry: AssetEntryUI): string {
  if (entry.isExcluded) return "Excluded";
  return entry.assetStatus;
}

function assetsSheet(
  entries: AssetEntryUI[],
  disclosureShort: string,
): ExportSheet {
  const active = entries.filter((e) => e.status === "active");
  const sorted = [...active].sort((a, b) => {
    const bySecured =
      Number(b.subjectToSecurityInterest) - Number(a.subjectToSecurityInterest);
    if (bySecured !== 0) return bySecured;
    const aVal = getSelectedValue(a) ?? getBestValue(a);
    const bVal = getSelectedValue(b) ?? getBestValue(b);
    return bVal - aVal;
  });

  // Exclude the raw "ROCAP" and "Director Estimate" sources — the dedicated
  // ROCAP column already carries the resolved director figure, so those raw
  // breakdown columns would duplicate it (matches the on-screen table).
  const valuationSources = collectValuationSources(active).filter(
    (source) => !isHiddenValuationSource(source),
  );

  return {
    name: "Assets",
    headers: [
      "Description",
      "Type",
      "Status",
      "Secured",
      "Flags",
      disclosureShort,
      "Practitioner",
      "Low Est.",
      "High Est.",
      ...valuationSources.map(formatSourceType),
    ],
    rows: sorted.map((entry) => [
      entry.description,
      ASSET_TYPE_LABEL[entry.assetType] ?? entry.assetType,
      statusLabel(entry),
      entry.subjectToSecurityInterest ? "Secured" : "Unsecured",
      flagsLabel(entry),
      money(getRocapValue(entry)),
      money(getPractitionerValue(entry)),
      money(getLowRecovery(entry)),
      money(getHighRecovery(entry)),
      ...valuationSources.map((source) =>
        money(getValuationForSource(entry, source)),
      ),
    ]),
  };
}

export function buildAssetsExport(
  entries: AssetEntryUI[],
  /**
   * Director-disclosure column header for the job's appointment type
   * (defaults to "ROCAP" for corporate insolvencies).
   */
  disclosureShort = "ROCAP",
): ExportContent {
  return {
    title: "Assets",
    sheets: [assetsSheet(entries, disclosureShort)],
  };
}
